import { Link } from "react-router-dom";
import { Helmet } from "react-helmet";
import { SITE_URL } from "./App";

const capabilities = [
  "Income tax return filing for individuals, businesses and corporations",
  "SECP company registration for Private Limited and Single Member Company structures",
  "FBR, SRB, PRA and BRA compliance, sales tax registration and monthly return support",
  "International bookkeeping, accounts, bank reconciliations and management reporting",
  "Tax notice response, audit preparation and regulatory documentation",
  "PSEB registration, renewals and profile updates for IT companies and freelancers",
  "EOBI and PESSI registration support for growing teams",
  "Wealth reconciliation, asset declarations and income documentation",
];

const principles = [
  {
    title: "Clarity first",
    text: "Clients understand required documents, expected timelines and filing steps before work begins.",
  },
  {
    title: "Compliance minded",
    text: "Advice is aligned with the relevant tax authority, corporate law requirement and portal workflow.",
  },
  {
    title: "Confidential handling",
    text: "Financial, business and personal tax records are treated with practical discretion and care.",
  },
];

export default function About() {
  return (
    <>
      <Helmet>
        <title>About Axis Compliance Solutions | Tax Consultants in Lahore</title>
        <meta
          name="description"
          content="Learn about Axis Compliance Solutions, a Lahore-based consultancy with international bookkeeping and bank reconciliation experience plus nationwide FBR, SECP, taxation and compliance support across Pakistan."
        />
        <link rel="canonical" href={`${SITE_URL}/about`} />
        <meta property="og:title" content="About Axis Compliance Solutions | Tax Consultants in Lahore" />
        <meta
          property="og:description"
          content="Learn about Axis Compliance Solutions, a Lahore-based consultancy for bookkeeping, tax filing, registration and compliance support."
        />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={`${SITE_URL}/about`} />
        <meta property="og:image" content={`${SITE_URL}/about-consulting-meeting.png`} />
        <meta property="og:image:alt" content="Axis Compliance Solutions consultants reviewing compliance documents" />
        <meta name="twitter:title" content="About Axis Compliance Solutions | Tax Consultants in Lahore" />
        <meta
          name="twitter:description"
          content="Bookkeeping, tax filing, registration and compliance support for clients across Pakistan."
        />
        <meta name="twitter:image" content={`${SITE_URL}/about-consulting-meeting.png`} />
        <meta name="twitter:image:alt" content="Axis Compliance Solutions consultants reviewing compliance documents" />
      </Helmet>

      <section className="page-hero">
        <div className="mx-auto max-w-7xl px-4 lg:px-8">
          <p className="eyebrow text-cyan-200">About the firm</p>
          <h1>International bookkeeping experience with nationwide tax compliance support.</h1>
          <p>
            Axis Compliance Solutions supports individuals, startups, SMEs and corporate clients with
            international bookkeeping, bank reconciliation, tax filing, registration and
            regulatory compliance across Pakistan.
          </p>
        </div>
      </section>

      <section className="section bg-white">
        <div className="mx-auto grid max-w-7xl gap-10 px-4 lg:grid-cols-[0.9fr_1.1fr] lg:px-8">
          <div className="lg:pt-2">
            <p className="eyebrow">Who we are</p>
            <h2 className="section-title">A bookkeeping, tax and compliance partner for modern businesses.</h2>
            <div className="mt-8 overflow-hidden rounded-md border border-slate-200 shadow-sm">
              <img
                src="/about-consulting-meeting.png"
                alt="Axis Compliance Solutions consultants reviewing compliance documents with a client"
                className="h-full min-h-[260px] w-full object-cover"
              />
            </div>
          </div>
          <div className="space-y-5 text-lg leading-8 text-slate-600">
            <p>
              We help clients simplify international bookkeeping, bank reconciliation, local
              filings, registrations and compliance obligations across FBR, SECP, SRB, PRA, BRA,
              PSEB and other business-facing portals. The goal is to keep your tax position,
              company records and financial documentation aligned.
            </p>
            <p>
              Whether you are reconciling bank activity, organizing accounting records,
              registering a company, filing an income tax return or responding to a notice, our
              work is built around accuracy, transparency and dependable follow-through.
            </p>
          </div>
        </div>
      </section>

      <section className="section bg-slate-100">
        <div className="mx-auto max-w-7xl px-4 lg:px-8">
          <div className="grid gap-5 md:grid-cols-3">
            {principles.map((principle) => (
              <article key={principle.title} className="service-card bg-white">
                <h3>{principle.title}</h3>
                <p>{principle.text}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="section bg-white">
        <div className="mx-auto max-w-7xl px-4 lg:px-8">
          <div className="max-w-3xl">
            <p className="eyebrow">Capabilities</p>
            <h2 className="section-title">Full-service support for tax, registration and compliance needs.</h2>
          </div>
          <div className="mt-10 grid gap-4 sm:grid-cols-2">
            {capabilities.map((item) => (
              <div key={item} className="flex gap-3 border-b border-slate-200 pb-4">
                <span className="mt-2 h-2 w-2 shrink-0 rounded-full bg-cyan-700" />
                <p className="text-slate-700">{item}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="section bg-slate-950 text-white">
        <div className="mx-auto max-w-4xl px-4 text-center lg:px-8">
          <h2 className="text-3xl font-semibold">Make your tax journey easier to manage.</h2>
          <p className="mt-4 text-slate-300">
            Book a consultation for international bookkeeping, bank reconciliation, tax returns,
            SECP registration, FBR compliance or audit support.
          </p>
          <Link to="/contact" className="btn-light mt-7">
            Contact the Team
          </Link>
        </div>
      </section>
    </>
  );
}
